import getNestedPropertyValue from "./getNestedPropertyValue";

/**
 * Token information for a template string
 */
interface TokenInfo {
  /** Array of token keys found in the template */
  paths: string[];
  /** Regex pattern for matching tokens */
  pattern: RegExp;
}

/**
 * Static cache mapping template strings to their token information
 */
const templateCache = new Map<string, TokenInfo>();

/**
 * Extracts token keys from a template string
 */
function extractTokenPaths(template: string): string[] {
  const paths: string[] = [];
  const regex = /\{([^}]+)\}/g;
  let match;
  while ((match = regex.exec(template)) !== null) {
    paths.push(match[1]);
  }
  return paths;
}

/**
 * Gets or creates token information from cache
 */
function getTokenInfo(template: string): TokenInfo {
  let tokenInfo = templateCache.get(template);
  if (!tokenInfo) {
    tokenInfo = {
      paths: extractTokenPaths(template),
      pattern: /\{([^}]+)\}/g,
    };
    templateCache.set(template, tokenInfo);
  }
  return tokenInfo;
}

/**
 * Formats a string template by replacing placeholders with values from the provided object.
 * Supports dot notation for accessing nested properties.
 *
 * @param stringTemplate - Template string with placeholders in the format {key} or {nested.key}
 * @param values - Object containing the values to substitute
 * @returns Formatted string with placeholders replaced
 *
 * @example
 * formatString("{name}", { name: "John" }) // "John"
 * formatString("{user.name}", { user: { name: "John" } }) // "John"
 * formatString("{address.city.name}", { address: { city: { name: "NYC" } } }) // "NYC"
 */
export function formatString(stringTemplate: string, values: Record<string, any>, observedPaths?: string[]): string {
  // Quick check: if no braces, no tokens to replace
  if (!stringTemplate.includes("{") || !stringTemplate.includes("}")) {
    return stringTemplate;
  }

  const tokenInfo = getTokenInfo(stringTemplate);

  // If no tokens found, return template as-is
  if (tokenInfo.paths.length === 0) {
    return stringTemplate;
  }

  // Collect observed paths if requested
  if (observedPaths !== undefined) {
    for (const key of tokenInfo.paths) {
      observedPaths.push(key);
    }
  }

  return stringTemplate.replace(tokenInfo.pattern, (match, key) => {
    // Support dot notation to traverse nested object properties (e.g., "user.name" -> values.user.name)
    const value = getNestedPropertyValue(key, values);
    return value != null ? String(value) : "";
  });
}
